import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Html5Qrcode } from 'html5-qrcode';
import { DoorOpen, ScanLine } from 'lucide-react';

import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { PunchupPageContent } from 'features/common/components/Punchup/primitives/layout/PunchupPageContent';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { MethodSwitcher } from './components/MethodSwitcher';
import { PunchupFileDropZone } from 'features/common/components/Punchup/primitives/upload/PunchupFileDropZone';
import { PunchupEventSummaryTile } from 'features/common/components/Punchup/primitives/display/PunchupEventSummaryTile';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { IdParamsType } from 'utils/types';
import { airbrake } from 'ErrorBoundary';

import { validateEncryptedTicket } from './services';
import { buildScanResult, extractValidationError, parseQrPayload } from './utils';
import { ManualCheckInState, ScanResultState, ValidationMethod } from './types';
import { useScannerGate } from './useScannerGate';
import { PunchupScanResult } from './PunchupScanResult';
import { ManualApprovalPanel } from './components/ManualApprovalPanel';
import { ManualCheckInResult } from './components/ManualCheckInResult';
import styles from './PunchupScanner.module.css';

/** DOM id html5-qrcode attaches its <video>/canvas to — it reads this via getElementById at construction. */
const QR_REGION_ID = 'punchup-qr-reader';
/** Camera decode rate — frames per second html5-qrcode samples for a QR. */
const SCAN_FPS = 10;
/** Square scan-box edge (px) drawn over the video; the QR must fit inside it to decode. */
const QR_BOX_SIZE = 250;

/** Green door check-in page. A left column carries the show-summary tile + the chosen **validation method**
 * surface (Camera / Photo / Manual approval); the right column shows the **verdict** of the last check-in,
 * titled with the gate this device is attributed to ("{gate} Gate"). Camera scanning uses html5-qrcode
 * (React-17-safe); "Photo of the ticket" decodes an uploaded image via the same lib's `scanFile`; "Manual
 * approval" looks a guest up and checks them in by ticket id. Camera access inside the iframe is delegated by
 * the host `allow="camera"` Permissions-Policy — see docs/iframe-communication.md → "Camera permission".
 * (Per-type attendance lives on the Analytics Show-Day section, not here.) Full spec: docs/scan-tickets-redesign.md. */
export const PunchupScanner = () => {
	const { t } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();
	const { event } = useGetEvent();
	const { gateName } = useScannerGate(eventId);

	const [method, setMethod] = useState<ValidationMethod>('camera');
	const [scanResult, setScanResult] = useState<ScanResultState | null>(null);
	const [manualResult, setManualResult] = useState<ManualCheckInState | null>(null);
	const [cameraError, setCameraError] = useState(false);

	const scannerRef = useRef<Html5Qrcode | null>(null);
	// Guards against the camera firing the success callback on every frame while the mutation is in flight.
	const processingRef = useRef(false);

	const { mutate: sendCode, isLoading: isValidating } = useMutation(validateEncryptedTicket, {
		onSuccess: ({ data }) => {
			setScanResult(buildScanResult(data));
		},
		onError: (error: unknown) => {
			airbrake.notify({ error: 'Error validating scanned ticket', context: { eventId } });
			setScanResult({
				type: 'error',
				errors: [
					{ error: { Error: extractValidationError(error) || t('scanner-validation-failed', 'Validation failed') } },
				],
			});
		},
	});

	const handleDecodedText = useCallback(
		(raw: string) => {
			if (processingRef.current) return;
			processingRef.current = true;

			const payload = parseQrPayload(raw);
			if (!payload) {
				airbrake.notify({ error: 'Scanned QR payload is invalid', context: { eventId } });
				setScanResult({
					type: 'error',
					errors: [{ error: { Error: t('scanner-invalid-format', 'Invalid QR code format') } }],
				});
				return;
			}
			sendCode({ qrData: payload, eventID: Number(eventId) });
		},
		[eventId, sendCode, t]
	);

	// Camera lifecycle: start while the camera method is active and no result is shown; stop on result /
	// method switch / unmount. Re-running on reset restarts it.
	//
	// The bootstrap is DEFERRED into a microtask and fully wrapped: html5-qrcode's `new Html5Qrcode()` and
	// `.start()` touch the reader DOM node *synchronously* (start() reads element.clientWidth/.style before it
	// returns its promise), so a missing/raced node throws synchronously. If that threw inside the effect body it
	// would abort React's effect-flush batch and skip the sibling PunchupPageShell `page-ready` effect — the host
	// would then never lift its loading cover and would reload us back to the dashboard after ~12s. Deferring to a
	// microtask moves the bootstrap out of the flush so `page-ready` always posts, and the try/catch + node guard
	// degrade any camera failure to the photo-upload fallback instead of crashing the page.
	useEffect(() => {
		if (method !== 'camera' || scanResult || cameraError) return;

		let cancelled = false;
		let html5: Html5Qrcode | null = null;

		const failToFallback = () => {
			if (cancelled) return;
			airbrake.notify({ error: 'Camera initialization failed for scanner', context: { eventId } });
			setCameraError(true);
		};

		Promise.resolve()
			.then(() => {
				if (cancelled) return undefined;
				const region = document.getElementById(QR_REGION_ID);
				if (!region) throw new Error('QR reader element not found');
				html5 = new Html5Qrcode(QR_REGION_ID);
				scannerRef.current = html5;
				return html5.start(
					{ facingMode: 'environment' },
					{ fps: SCAN_FPS, qrbox: { width: QR_BOX_SIZE, height: QR_BOX_SIZE } },
					handleDecodedText,
					undefined
				);
			})
			.catch(failToFallback);

		return () => {
			cancelled = true;
			// stop() rejects if start() never ran/succeeded — that path is expected, so swallow it.
			const instance = html5;
			if (instance) {
				instance
					.stop()
					.then(() => instance.clear())
					.catch(() => undefined);
			}
		};
	}, [method, scanResult, cameraError, handleDecodedText, eventId]);

	/** Decode a QR from an uploaded image via the same lib + same validation flow (the Photo method, and the
	 * camera-unavailable fallback). A decode failure surfaces in the right-column verdict panel (large + clear),
	 * not as small text under the drop zone. */
	const handleImageFile = useCallback(
		async (file: File) => {
			processingRef.current = false;
			const html5 = scannerRef.current ?? new Html5Qrcode(QR_REGION_ID);
			scannerRef.current = html5;
			try {
				const decoded = await html5.scanFile(file, false);
				handleDecodedText(decoded);
			} catch {
				setScanResult({
					type: 'error',
					errors: [{ error: { Error: t('scanner-no-qr', 'No QR code found in image') } }],
				});
			}
		},
		[handleDecodedText, t]
	);

	const resetScanner = () => {
		processingRef.current = false;
		setCameraError(false);
		setScanResult(null);
	};

	const methodOptions = useMemo(
		() => [
			{ value: 'camera' as ValidationMethod, label: t('scanner-method-camera-short', 'Camera') },
			{ value: 'photo' as ValidationMethod, label: t('scanner-method-photo-short', 'Photo') },
			{ value: 'manual' as ValidationMethod, label: t('scanner-method-manual-short', 'Manual') },
		],
		[t]
	);

	const idleText: Record<ValidationMethod, string> = {
		camera: t('scanner-idle-camera', 'Point the camera at a ticket to check the guest in.'),
		photo: t('scanner-idle-photo', 'Upload a photo of the ticket to check the guest in.'),
		manual: t('scanner-idle-manual', 'Find a guest and check them in — the result shows here.'),
	};

	// Pre-formatted show date for the summary tile (the tile is domain-free; the page formats from EventData).
	const dateLabel = event?.startDate
		? new Date(event.startDate).toLocaleString('en-US', {
				weekday: 'short',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
		  })
		: undefined;
	const location = event?.venue || event?.streetAddress || undefined;

	const photoSurface = (
		<PunchupFileDropZone
			accept="image/*"
			onFileSelect={handleImageFile}
			supportedTypesLabel={t('scanner-photo-types', 'A photo or screenshot showing the ticket QR code')}
		/>
	);

	// Right-column verdict: per method. Manual uses its concise local-data confirmation (no rescan / no
	// misleading "not scanned" status); camera/photo use the QR result card. Each method keeps its own last
	// verdict, with a calm idle prompt before the first.
	const idlePanel = (
		<div className={styles.idlePanel} role="status">
			<ScanLine size={32} className={styles.idleIcon} aria-hidden="true" />
			<p className={styles.idleText}>{idleText[method]}</p>
		</div>
	);
	const resultColumn =
		method === 'manual' ? (
			manualResult ? (
				<ManualCheckInResult result={manualResult} />
			) : (
				idlePanel
			)
		) : scanResult ? (
			<PunchupScanResult result={scanResult} onReset={resetScanner} />
		) : (
			idlePanel
		);

	return (
		<PunchupPageShell pageName="Scan Tickets">
			<PunchupPageContent>
				<div className={styles.formContainer}>
					<div className={styles.leftColumn}>
						{event?.name && <PunchupEventSummaryTile title={event.name} dateLabel={dateLabel} location={location} />}

						<MethodSwitcher
							value={method}
							onChange={setMethod}
							options={methodOptions}
							label={t('scanner-method-title', 'Validation method')}
						/>

						<div className={styles.methodSurface}>
							{/* Always mounted: html5-qrcode resolves this id at construction, including for the photo decode. */}
							<div
								id={QR_REGION_ID}
								className={method === 'camera' && !cameraError ? styles.reader : styles.readerHidden}
							/>
							{isValidating && (
								<div className={styles.validating} role="status">
									<PunchupSpinner size={20} />
									<span>{t('scanner-validating', 'Validating ticket…')}</span>
								</div>
							)}
							{method === 'camera' && cameraError && (
								<>
									<p className={styles.fallbackText}>
										{t('scanner-no-camera', 'No camera available. Upload a photo of the ticket QR code instead:')}
									</p>
									{photoSurface}
								</>
							)}
							{method === 'photo' && photoSurface}
							{method === 'manual' && <ManualApprovalPanel eventId={eventId} onResult={setManualResult} />}
						</div>
					</div>

					<div className={styles.resultColumn}>
						{/* The verdict box is titled with the gate this device is attributed to (resolved server-side;
						    hidden until known / on a fetch error). */}
						{gateName && (
							<p className={styles.gateTitle}>
								<DoorOpen size={16} aria-hidden="true" />
								{t('scanner-gate-title', '{{gate}} Gate', { gate: gateName })}
							</p>
						)}
						{resultColumn}
					</div>
				</div>
			</PunchupPageContent>
		</PunchupPageShell>
	);
};
